import React, { createContext, useContext, useState, useMemo, useCallback, useRef } from 'react';
import { mergeBannerWithMenuDb } from '../utils/comm/bannerFromMenuDb';

/**
 * @typedef {{ showSubmenu?: boolean, showFilter?: boolean }} LeftSidebarOptions
 */

/**
 * 페이지 레이아웃 컨텍스트
 * 배너, 사이드바, 좌측 사이드바 필터 영역(옵션) 정보를 MainLayout/Sidebar에 전달
 * contentLoading: true 시 MainLayout에서 공통 스켈레톤 UI 표시 (DB 조회 전)
 */
const PageLayoutContext = createContext({
  bannerData: null,
  setBannerData: () => {},
  setMenuDataForBanner: () => {},
  setBannerPathname: () => {},
  sidebarVisible: true,
  setSidebarVisible: () => {},
  hideSidebar: () => {},
  showSidebar: () => {},
  /** 좌측 서브메뉴 하단 필터 영역에 넣을 컴포넌트 (옵션, 페이지에서 설정) */
  leftSidebarFilterContent: null,
  setLeftSidebarFilterContent: () => {},
  /** 좌측 서브메뉴 표시 여부 등 (showSubmenu, showFilter, 페이지에서 설정) */
  leftSidebarOptions: null,
  setLeftSidebarOptions: () => {},
  /** 페이지 콘텐츠 로딩 여부 (true 시 MainLayout에서 스켈레톤 표시) */
  contentLoading: false,
  setContentLoading: () => {},
  /** 스켈레톤 variant: 'cardList' | 'grid' | 'table' | 'content' (페이지에서 선택) */
  contentSkeletonVariant: 'cardList',
  setContentSkeletonVariant: () => {},
  /** 스켈레톤 옵션 (showTabs 등, 페이지에서 선택) */
  contentSkeletonProps: {},
  setContentSkeletonProps: () => {},
});

export const usePageLayout = () => {
  return useContext(PageLayoutContext);
};

export const PageLayoutProvider = ({ children }) => {
  const [bannerData, setBannerDataState] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(true); // 기본값: 보이기
  const [leftSidebarFilterContent, setLeftSidebarFilterContent] = useState(null);
  const [leftSidebarOptions, setLeftSidebarOptions] = useState(null); // { showSubmenu?, showFilter? }
  const [contentLoading, setContentLoading] = useState(false);
  const [contentSkeletonVariant, setContentSkeletonVariant] = useState('cardList');
  const [contentSkeletonProps, setContentSkeletonProps] = useState({});
  const hideSidebar = useCallback(() => setSidebarVisible(false), []);
  const showSidebar = useCallback(() => setSidebarVisible(true), []);

  /** 페이지가 마지막으로 전달한 setBannerData 원본(병합 입력) */
  const rawBannerPayloadRef = useRef(null);
  const menuDataRef = useRef([]);
  const bannerPathnameRef = useRef('');

  const recomputeBanner = useCallback(() => {
    const raw = rawBannerPayloadRef.current;
    if (raw == null) {
      return;
    }
    const merged = mergeBannerWithMenuDb(
      bannerPathnameRef.current,
      menuDataRef.current,
      raw
    );
    if (merged.merged) {
      setBannerDataState({
        title: merged.title,
        breadcrumbPath: merged.breadcrumbPath,
        ...(merged.loading !== undefined ? { loading: merged.loading } : {}),
      });
    } else {
      setBannerDataState({ ...raw });
    }
  }, []);

  const setBannerData = useCallback((payload) => {
    if (payload == null) {
      rawBannerPayloadRef.current = null;
      setBannerDataState(null);
      return;
    }
    rawBannerPayloadRef.current = payload;
    recomputeBanner();
  }, [recomputeBanner]);

  const setMenuDataForBanner = useCallback(
    (menuData) => {
      menuDataRef.current = Array.isArray(menuData) ? menuData : [];
      recomputeBanner();
    },
    [recomputeBanner]
  );

  const setBannerPathname = useCallback(
    (pathname) => {
      bannerPathnameRef.current = pathname && typeof pathname === 'string' ? pathname : '';
      recomputeBanner();
    },
    [recomputeBanner]
  );

  // value 객체를 useMemo로 메모이제이션하여 불필요한 리렌더링 방지
  const value = useMemo(
    () => ({
      bannerData,
      setBannerData,
      setMenuDataForBanner,
      setBannerPathname,
      sidebarVisible,
      setSidebarVisible,
      hideSidebar,
      showSidebar,
      leftSidebarFilterContent,
      setLeftSidebarFilterContent,
      leftSidebarOptions,
      setLeftSidebarOptions,
      contentLoading,
      setContentLoading,
      contentSkeletonVariant,
      setContentSkeletonVariant,
      contentSkeletonProps,
      setContentSkeletonProps,
    }),
    [
      bannerData,
      setBannerData,
      setMenuDataForBanner,
      setBannerPathname,
      sidebarVisible,
      hideSidebar,
      showSidebar,
      leftSidebarFilterContent,
      leftSidebarOptions,
      contentLoading,
      contentSkeletonVariant,
      contentSkeletonProps,
    ]
  );

  return (
    <PageLayoutContext.Provider value={value}>
      {children}
    </PageLayoutContext.Provider>
  );
};
